import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchIssueTypes } from '@/services/conversationService';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: string, issueTypes: string) => void;
  feedbackType: 'positive' | 'negative';
}

export default function FeedbackModal({
  isOpen,
  onClose,
  onSubmit,
  feedbackType,
}: FeedbackModalProps) {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState('');
  const [issueTypes, setIssueTypes] = useState('');
  const [issueTypeOptions, setIssueTypeOptions] = useState<
    { value: string; label: string }[]
  >([{ value: '', label: t('feedback.selectOption') }]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  // Fetch issue types from API
  useEffect(() => {
    if (isOpen && feedbackType === 'negative') {
      const loadIssueTypes = async () => {
        setIsLoadingOptions(true);
        try {
          const data: any = await fetchIssueTypes();
          const types = (data?.issueTypes || []).map((it: string) => ({
            value: it,
            label: it,
          }));
          setIssueTypeOptions([
            { value: '', label: t('feedback.selectOption') },
            ...types,
          ]);
        } catch (error) {
          console.error('Failed to fetch issue types:', error);
          // Fallback to default options if API fails
          setIssueTypeOptions([
            { value: '', label: t('feedback.selectOption') },
            { value: 'other', label: t('feedback.other') },
          ]);
        } finally {
          setIsLoadingOptions(false);
        }
      };
      loadIssueTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, feedbackType]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit(feedback, issueTypes);
    setFeedback('');
    setIssueTypes('');
    onClose();
  };

  const handleCancel = () => {
    setFeedback('');
    setIssueTypes('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleCancel}
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{t('feedback.title')}</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {/* Issue Types Dropdown - Only for negative feedback */}
          {feedbackType === 'negative' && (
            <div>
              <label className="block text-sm text-gray-600 mb-2">
                {t('feedback.issueTypeLabel')}
              </label>
              <div className="relative">
                <select
                  value={issueTypes}
                  onChange={e => setIssueTypes(e.target.value)}
                  disabled={isLoadingOptions}
                  className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.25rem',
                  }}
                >
                  {issueTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Feedback Text */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">
              {t('feedback.detailsLabel')}
            </label>
            <textarea
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder={
                feedbackType === 'positive'
                  ? t('feedback.positivePlaceholder')
                  : t('feedback.negativePlaceholder')
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none h-24"
            />
          </div>

          {/* Info Text */}
          <p className="text-xs text-gray-500">
            Submitting this report will send the entire current conversation to
            HopeAI for future improvements to our models.{' '}
            {/* <a href="#" className="text-blue-600 hover:underline">
              Learn More
            </a> */}
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800 transition-colors"
          >
            {t('common.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
